import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { motion, useMotionValue, useTransform, PanInfo, animate } from "framer-motion";
import { ArrowLeft, ArrowRight, ChevronLeft, Loader2, Megaphone, Play as PlayIcon, Pause as PauseIcon, Pencil, Star, Check, X, RotateCcw, Sparkles, Shuffle } from "lucide-react";
import { Deck } from "@/types/linguadeck";
import { useIsMobile } from "@/hooks/use-mobile";
import { useGamification } from "@/contexts/GamificationContext";
import { useSoundStore } from "@/stores/useSoundStore";
import { getCardSkinStyles } from "@/lib/cardSkins";
import PronunciationScoreModal from "./PronunciationScoreModal";
import { calculateSM2 } from "@/utils/srs";
import { updateCardSrs, addCardFavorite, removeCardFavorite, fetchFavoriteCardIds, recordCardInteraction } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { playAudio } from "@/lib/audioUtils";
import { resolveCardSideSpeechLocale } from "@/lib/speechText";
import { getMeaningBasedHint, getFrontSideLang, getBackSideLang } from "@/lib/flashcardHint";
import EditSingleCardModal from "./EditSingleCardModal";

interface FlashcardViewProps {
  deck: Deck;
  cardIndex: number;
  cardFlipped: boolean;
  isTeacher: boolean;
  onFlip: () => void;
  onMark: (correct: boolean) => void;
  onShuffle: () => void;
  onShowExplain: () => void;
  onPrevCard?: () => void;
  onNextCard?: () => void;
  onBack?: () => void;
  /** Called when user marks "Know" — pass interval in days for optional "next review" toast */
  onMarkedKnow?: (nextReviewDays: number) => void;
  /** Called when user saves a single-card edit. Pass (cardIndex, updates) to update local state. */
  onUpdateCard?: (
    cardIndex: number,
    updates: Partial<{
      front: string;
      back: string;
      frontLang?: string | null;
      backLang?: string | null;
      frontAudioLocale?: string | null;
      backAudioLocale?: string | null;
      frontAudioUrl?: string | null;
      backAudioUrl?: string | null;
      imageUrl?: string | null;
      definition?: string | null;
      example?: string | null;
    }>
  ) => void;
  /** Read-only mode for shared deck links: hide Edit, Favorite, Record, Shuffle, Auto-play; show Correct/Incorrect when flipped */
  readOnly?: boolean;
  /** Mobile fullscreen mode: 100dvh layout, swipe gestures, minimal UI */
  fullscreenMobile?: boolean;
  /** Undo last swipe (cancel last card advance) */
  onUndo?: () => void;
  /** Whether undo is available */
  canUndo?: boolean;
  /** Open settings from study mode header */
  onOpenSettings?: () => void;
  /** Called when this component mounts — parent should reset cardFlipped to false so card always shows front first. */
  onMountReset?: () => void;
  /** Called after SRS is persisted to DB — use to sync local deck state (e.g. mergeCardInDeck).
   * cardId is passed so parent can resolve correct index when deck is filtered (e.g. due cards). */
  onCardSrsUpdated?: (
    cardIndex: number,
    cardId: string | undefined,
    srsData: { repetition_count: number; easiness_factor: number; interval_days: number; next_review: string }
  ) => void;
}

export default function FlashcardView({
  deck,
  cardIndex,
  cardFlipped,
  isTeacher,
  onFlip,
  onMark,
  onShuffle,
  onShowExplain,
  onPrevCard,
  onNextCard,
  onBack,
  onMarkedKnow,
  onUpdateCard,
  readOnly = false,
  fullscreenMobile = false,
  onUndo,
  canUndo = false,
  onOpenSettings,
  onMountReset,
  onCardSrsUpdated,
}: FlashcardViewProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const { voiceVolume } = useSoundStore();
  const [isHintExpanded, setIsHintExpanded] = useState(false);
  const [isAutoPlaying, setIsAutoPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [pronunciationResult, setPronunciationResult] = useState<{ said: string; score: number } | null>(null);
  const [isEditingSingleCard, setIsEditingSingleCard] = useState(false);
  const [loadingAudioFor, setLoadingAudioFor] = useState<"front" | "back" | null>(null);
  const [audioOverrides, setAudioOverrides] = useState<
    Record<
      string,
      {
        frontAudioUrl?: string | null;
        backAudioUrl?: string | null;
        frontAudioLocale?: string | null;
        backAudioLocale?: string | null;
      }
    >
  >({});

  const { equippedCardSkin, finishStudySession } = useGamification();
  const cardSkinStyles = getCardSkinStyles(equippedCardSkin);

  const sessionCompletedRef = useRef<string | null>(null);
  const cardShownAtRef = useRef<number>(Date.now());
  useEffect(() => {
    sessionCompletedRef.current = null;
  }, [deck.id]);
  useEffect(() => {
    cardShownAtRef.current = Date.now();
  }, [cardIndex]);
  useEffect(() => {
    setIsHintExpanded(false);
    setLoadingAudioFor(null);
  }, [cardIndex]);

  // Hard reset: on mount, ensure parent resets flip state so card always shows front first
  useEffect(() => {
    onMountReset?.();
  }, [onMountReset]);
  const [favoritedIds, setFavoritedIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    setAudioOverrides({});
  }, [deck.id]);

  useEffect(() => {
    if (!user?.id || readOnly || !deck.cards?.length) return;
    const ids = deck.cards.map((c) => c.id).filter((id): id is string => !!id);
    if (ids.length === 0) return;
    fetchFavoriteCardIds(user.id, ids).then(setFavoritedIds).catch(() => {});
  }, [user?.id, deck.id, deck.cards, readOnly]);

  const card = deck.cards[cardIndex];
  const cardId = card.id ?? `idx-${cardIndex}`;
  const audioOverride = audioOverrides[cardId];
  const playbackCard = audioOverride ? { ...card, ...audioOverride } : card;

  // Invalidate stale audio when card's frontLang/backLang changes (e.g. after edit-card save) so TTS uses new language
  const prevCardLangRef = useRef<{ cardId: string; frontLang: string; backLang: string } | null>(null);
  useEffect(() => {
    const fid = card?.id ?? `idx-${cardIndex}`;
    const frontLang = (card?.frontLang ?? "").trim();
    const backLang = (card?.backLang ?? "").trim();
    const prev = prevCardLangRef.current;
    const langChanged =
      prev && prev.cardId === fid && (prev.frontLang !== frontLang || prev.backLang !== backLang);
    prevCardLangRef.current = { cardId: fid, frontLang, backLang };
    if (langChanged && fid) {
      setAudioOverrides((o) => {
        if (!o[fid]) return o;
        const next = { ...o };
        delete next[fid];
        return next;
      });
      if (typeof console !== "undefined" && console.info) {
        console.info("[FlashcardView] study_card_audio_invalidated_after_lang_change", { cardId: fid });
        console.info("[FlashcardView] card_audio_invalidated_after_language_fix", { cardId: fid });
      }
    }
  }, [card?.id, card?.frontLang, card?.backLang, cardIndex]);
  const total = deck.cards.length;
  const pct = total > 0 ? Math.round(((cardIndex + 1) / total) * 100) : 0;

  const handleMark = async (correct: boolean) => {
    if (!readOnly) {
      const quality = correct ? 4 : 1;
      const rep = card.repetition_count ?? 0;
      const ef = card.easiness_factor ?? 2.5;
      const interval = card.interval_days ?? 0;
      const result = calculateSM2(quality, rep, ef, interval);

      if (card.id) {
        try {
          console.log("[SWIPE DEBUG] Saving progress for card:", card.id, "Status:", correct ? "known" : "repeat");
          const srsData = {
            repetition_count: result.repetitionCount,
            easiness_factor: result.easinessFactor,
            interval_days: result.intervalDays,
            next_review: result.nextReviewDate.toISOString(),
          };
          await updateCardSrs(card.id, srsData);
          onCardSrsUpdated?.(cardIndex, card.id, srsData);
        } catch (err) {
          console.warn("[SRS] Failed to update card:", err);
        }
      }

      if (correct && onMarkedKnow) {
        onMarkedKnow(result.intervalDays);
      }

      if (user?.id && card.id && deck.id) {
        const deckIdForRecord = (card as Card & { deckId?: string }).deckId ?? deck.id;
        const responseTimeMs = Math.round(Date.now() - cardShownAtRef.current);
        recordCardInteraction({
          userId: user.id,
          cardId: card.id,
          deckId: deckIdForRecord,
          mode: "cards",
          answeredCorrectly: correct,
          responseTimeMs: responseTimeMs >= 0 ? responseTimeMs : undefined,
          hintUsed: isHintExpanded,
          flipped: cardFlipped,
        }).catch((err) => console.warn("[Adaptive] recordCardInteraction failed:", err));
      }

      const isLastCard = cardIndex === total - 1;
      const sessionKey = `${deck.id}-${total}`;
      if (isLastCard && sessionCompletedRef.current !== sessionKey) {
        sessionCompletedRef.current = sessionKey;
        finishStudySession(total, false);
      }
    }
    onMark(correct);
  };

  // Auto-play: 1) Wait on front (question time), 2) Flip to back, 3) Wait (answer time), 4) Mark Know. Parent resets cardFlipped to false when advancing.
  const autoPlayTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    if (readOnly || !isAutoPlaying || total === 0) return;
    if (cardIndex >= total) return;

    const isLast = cardIndex >= total - 1;
    const QUESTION_TIME_MS = 2500;
    const ANSWER_TIME_MS = 3000;

    if (!cardFlipped) {
      // Phase 1: Show front, wait X sec (reading question)
      autoPlayTimeoutRef.current = setTimeout(() => {
        onFlip();
        // Phase 2: Show back, wait Y sec (reading answer)
        autoPlayTimeoutRef.current = setTimeout(() => {
          if (isLast) setIsAutoPlaying(false);
          handleMark(true);
          // Parent resets cardFlipped to false for next card
        }, ANSWER_TIME_MS);
      }, QUESTION_TIME_MS);
    } else {
      // Already on back (e.g. user flipped manually) - wait and advance
      autoPlayTimeoutRef.current = setTimeout(() => {
        if (isLast) setIsAutoPlaying(false);
        handleMark(true);
      }, ANSWER_TIME_MS);
    }

    return () => {
      if (autoPlayTimeoutRef.current) {
        clearTimeout(autoPlayTimeoutRef.current);
        autoPlayTimeoutRef.current = null;
      }
    };
  }, [isAutoPlaying, cardIndex, cardFlipped, total]);

  const recordPronunciation = () => {
    const win = window as Window & { SpeechRecognition?: typeof SpeechRecognition; webkitSpeechRecognition?: typeof SpeechRecognition };
    const SR = win.SpeechRecognition || win.webkitSpeechRecognition;
    if (!SR) {
      alert(t("flashcard.browserNoRecording"));
      return;
    }
    setIsRecording(true);
    const rec = new SR();
    rec.lang = resolveCardSideSpeechLocale(playbackCard, cardFlipped ? "back" : "front", deck.lang, "en-US");
    rec.start();
    rec.onresult = (e: any) => {
      const said = e.results[0][0].transcript;
      const score = Math.floor(Math.random() * 3) + 8;
      setIsRecording(false);
      setPronunciationResult({ said, score });
    };
    rec.onerror = () => setIsRecording(false);
  };

  const currentSide = cardFlipped ? "back" : "front";
  const deckLang = deck.lang ?? "en";
  const hint = getMeaningBasedHint(card, deckLang, currentSide);
  const isFavorited = favoritedIds.has(cardId);

  // Debug: hint side/language resolution (no card content)
  const prevFlippedRef = useRef<boolean | null>(null);
  useEffect(() => {
    const frontLang = getFrontSideLang(card, deckLang);
    const backLang = getBackSideLang(card, deckLang);
    const resolvedLang = currentSide === "front" ? frontLang : backLang;
    if (typeof console !== "undefined" && console.info) {
      console.info("[FlashcardView] card_hint_render_started", { cardId, side: currentSide });
      console.info("[FlashcardView] card_hint_side_resolved", { side: currentSide });
      console.info("[FlashcardView] card_hint_language_resolved", { side: currentSide, lang: resolvedLang });
    }
    if (prevFlippedRef.current !== null && prevFlippedRef.current !== cardFlipped) {
      if (typeof console !== "undefined" && console.info) {
        console.info("[FlashcardView] card_hint_updated_after_flip", { side: currentSide, lang: resolvedLang });
      }
    }
    prevFlippedRef.current = cardFlipped;
  }, [cardId, currentSide, cardFlipped, card, deckLang]);
  const rememberAudioVariant = (side: "front" | "back", url: string, locale: string) => {
    setAudioOverrides((current) => ({
      ...current,
      [cardId]: {
        ...current[cardId],
        ...(side === "front"
          ? {
              frontAudioUrl: url,
              frontAudioLocale: locale,
            }
          : {
              backAudioUrl: url,
              backAudioLocale: locale,
            }),
      },
    }));
  };
  const toggleFavorite = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!card.id || readOnly) return;
    const next = new Set(favoritedIds);
    if (next.has(card.id)) next.delete(card.id);
    else next.add(card.id);
    setFavoritedIds(next);
    if (user?.id) {
      try {
        if (next.has(card.id)) {
          await addCardFavorite(user.id, card.id);
        } else {
          await removeCardFavorite(user.id, card.id);
        }
      } catch (err) {
        console.warn("[Favorites] Failed to update:", err);
        setFavoritedIds(favoritedIds);
      }
    }
  };

  const quickActionBtnClass =
    "flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[hsl(var(--foreground))] dark:text-[hsl(var(--muted-foreground))]";
  const ttsBtnClass =
    "relative z-50 flex h-8 w-8 items-center justify-center rounded-full text-gray-400 transition-colors hover:text-[hsl(var(--foreground))] dark:text-[hsl(var(--muted-foreground))]";

  const handleFlip = () => onFlip();

  // Premium Tinder-style swipe — power-based flick detection + fluid physics
  const SWIPE_OFFSET_THRESHOLD = 100;
  const swipeConfidenceThreshold = 10000;
  const swipePower = (offset: number, velocity: number) => Math.abs(offset) * velocity;
  const EXIT_OFFSET = 500;
  const x = useMotionValue(0);
  const rotate = useTransform(x, [-250, 0, 250], [-12, 0, 12]);
  const opacityLeft = useTransform(x, [-250, -SWIPE_OFFSET_THRESHOLD, 0], [0.95, 0.5, 0]);
  const opacityRight = useTransform(x, [0, SWIPE_OFFSET_THRESHOLD, 250], [0, 0.5, 0.95]);
  const scaleLeft = useTransform(x, [-200, -60, 0], [1.15, 0.85, 0.7]);
  const scaleRight = useTransform(x, [0, 60, 200], [0.7, 0.85, 1.15]);
  // Dynamic background tint: green on swipe right, orange on swipe left (max opacity ~0.25)
  const opacityGreen = useTransform(x, [0, 80, 200], [0, 0.5, 1]);
  const opacityOrange = useTransform(x, [-200, -80, 0], [1, 0.5, 0]);
  const isDraggingRef = useRef(false);
  const isExitingRef = useRef(false);

  const resetCardPosition = () => {
    x.set(0);
  };

  const handleDragEnd = async (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    if (isExitingRef.current) return;
    const offset = info.offset.x;
    const velocity = info.velocity.x;
    const power = swipePower(offset, velocity);
    const isRightSwipe = power > swipeConfidenceThreshold || offset > SWIPE_OFFSET_THRESHOLD;
    const isLeftSwipe = power < -swipeConfidenceThreshold || offset < -SWIPE_OFFSET_THRESHOLD;
    const spring = { type: "spring" as const, stiffness: 200, damping: 25 };
    if (isRightSwipe) {
      isExitingRef.current = true;
      await animate(x, EXIT_OFFSET, spring);
      handleMark(true);
      isExitingRef.current = false;
      resetCardPosition();
    } else if (isLeftSwipe) {
      isExitingRef.current = true;
      await animate(x, -EXIT_OFFSET, spring);
      handleMark(false);
      isExitingRef.current = false;
      resetCardPosition();
    } else {
      animate(x, 0, spring);
    }
    isDraggingRef.current = false;
  };

  const handleDragStart = () => {
    isDraggingRef.current = true;
  };

  const handleTapFlip = () => {
    if (isDraggingRef.current || isExitingRef.current) return;
    handleFlip();
  };

  // Reset x when card index changes (new card)
  useEffect(() => {
    resetCardPosition();
  }, [cardIndex]);

  // Keyboard: Left/Right arrows for navigation
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isEditingSingleCard) return;
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        onPrevCard?.();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        onNextCard?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [isEditingSingleCard, onPrevCard, onNextCard]);

  // Dynamic font size for long text to prevent overflow (front: larger base for readability)
  const frontLen = card.front.length;
  const frontSizeClass = frontLen > 80 ? "text-2xl" : frontLen > 40 ? "text-3xl" : "text-4xl";
  const isReadOnlyMobile = readOnly && isMobile;
  const isReadOnlyDesktop = readOnly && !isMobile;

  const hasLongText =
    card.front.length > 120 ||
    card.back.length > 120 ||
    (card.definition ? card.definition.length > 180 : false) ||
    (card.example ? card.example.length > 180 : false);
  const hasVeryLongText =
    card.front.length > 220 ||
    card.back.length > 220 ||
    (card.definition ? card.definition.length > 260 : false) ||
    (card.example ? card.example.length > 260 : false);

  const cardShellHeightClass = isReadOnlyMobile
    ? // Mobile shared / read-only: немного выше карта, чтобы тексту хватало места,
      // но с запасом под нижние кнопки и CTA под карточкой.
      "min-h-[19rem] max-h-[64svh] sm:max-h-[68svh]"
    : isReadOnlyDesktop
      ? "min-h-[24rem] md:min-h-[30rem] max-h-[min(74vh,46rem)]"
      : "min-h-[300px] md:min-h-[max(490px,59vh)] max-h-[60vh] md:max-h-[78vh]";

  const mediaHeightClass = isReadOnlyMobile
    ? hasLongText
      ? // На мобильных shared карточках с длинным текстом делаем картинку компактнее.
        "max-h-[16vh] sm:max-h-[18vh]"
      : "max-h-[22vh] sm:max-h-[24vh]"
    : isReadOnlyDesktop
      ? "max-h-[26vh] xl:max-h-[30vh]"
      : "max-h-[40vh] md:max-h-[38vh]";
  const standardStageWidthClass = "md:max-w-[85vw]";
  const standardCardWidthClass = "max-w-[36rem] md:max-w-6xl";
  const readOnlyStageWidthClass = isReadOnlyDesktop
    ? "md:max-w-[50rem] xl:max-w-[54rem]"
    : "max-w-[32rem] md:max-w-6xl";
  const stageWidthClass = isReadOnlyDesktop ? readOnlyStageWidthClass : standardStageWidthClass;
  const cardWidthClass = isReadOnlyDesktop ? readOnlyStageWidthClass : standardCardWidthClass;
  const getCardTextStyle = (text: string): React.CSSProperties | undefined => {
    if (!isReadOnlyMobile) return undefined;

    if (text.length > 120) {
      return { fontSize: "1.02rem", lineHeight: 1.38 };
    }
    if (text.length > 80) {
      return { fontSize: "1.16rem", lineHeight: 1.36 };
    }
    if (text.length > 40) {
      return { fontSize: "1.45rem", lineHeight: 1.28 };
    }

    return { fontSize: "clamp(1.8rem, 7.4vw, 2.4rem)", lineHeight: 1.15 };
  };
  const frontTextStyle = getCardTextStyle(card.front);
  const backTextStyle = getCardTextStyle(card.back);

  useEffect(() => {
    if (!isReadOnlyMobile) return;
    if (typeof console === "undefined" || typeof console.info !== "function") return;

    console.info("[FlashcardView] shared_card_mobile_layout_applied", {
      cardId,
      hasLongText,
      hasVeryLongText,
      side: cardFlipped ? "back" : "front",
    });

    if (hasLongText) {
      console.info("[FlashcardView] shared_card_long_text_mode_enabled", {
        cardId,
        side: cardFlipped ? "back" : "front",
      });
    }

    if (hasVeryLongText) {
      console.info("[FlashcardView] shared_card_text_overflow_handled", {
        cardId,
        side: cardFlipped ? "back" : "front",
      });
    }
  }, [isReadOnlyMobile, cardId, hasLongText, hasVeryLongText, cardFlipped]);

  // Premium fullscreen Study Mode — gesture-driven, deck effect, Tinder-like swipes
  if (fullscreenMobile) {
    const nextCard = cardIndex + 1 < total ? deck.cards[cardIndex + 1] : null;
    const nextFrontLen = nextCard ? nextCard.front.length : 0;
    const nextSizeClass = nextFrontLen > 80 ? "text-2xl" : nextFrontLen > 40 ? "text-3xl" : "text-4xl";

    return (
      <div className="flex flex-col h-[100dvh] w-full bg-background overflow-hidden overscroll-x-none pt-[env(safe-area-inset-top)]" style={{ overscrollBehaviorX: "none" }}>
        {/* Thin progress bar at top */}
        <div className="flex-shrink-0 h-[3px] w-full bg-[hsl(var(--bg3))]">
          <motion.div
            className="h-full rounded-r-full"
            style={{
              width: `${pct}%`,
              background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
            }}
            transition={{ duration: 0.3 }}
          />
        </div>
        {/* Minimal header: Close (X), Progress */}
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-2.5 min-h-[44px]">
          <div className="min-w-[44px] flex justify-start">
            {onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex items-center justify-center w-11 h-11 rounded-full text-[hsl(var(--muted-foreground))] active:bg-[hsl(var(--bg3))] active:opacity-70 transition-colors touch-manipulation"
                aria-label={t("studyMode.close")}
              >
                <X size={22} strokeWidth={2} />
              </button>
            )}
          </div>
          <span className="text-sm font-medium text-[hsl(var(--foreground))] tabular-nums">
            {t("studyMode.progress", { current: cardIndex + 1, total })}
          </span>
          <div className="min-w-[44px] flex justify-end" />
        </div>

        {/* Card area: deck effect + elongated card, stack visible behind — overscroll-x-none blocks iOS swipe-back */}
        <div className="flex-1 min-h-0 flex items-center justify-center relative px-3 py-1.5 sm:px-4 sm:py-2 perspective-[1200px] overscroll-x-none" style={{ overscrollBehaviorX: "none" }}>
          {/* Swipe labels — above cards (z-[60]), pointer-events-none so drag works */}
          <motion.div
            className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-[60] pointer-events-none origin-center"
            style={{ opacity: opacityLeft, scale: scaleLeft }}
          >
            <span className="text-xl font-bold text-amber-500 dark:text-amber-400 whitespace-nowrap drop-shadow-sm">
              {t("studyMode.stillLearning")}
            </span>
          </motion.div>
          <motion.div
            className="absolute right-4 top-1/2 -translate-y-1/2 flex flex-col items-center gap-1 z-[60] pointer-events-none origin-center"
            style={{ opacity: opacityRight, scale: scaleRight }}
          >
            <span className="text-xl font-bold text-emerald-600 dark:text-emerald-400 whitespace-nowrap drop-shadow-sm">
              {t("studyMode.know")}
            </span>
          </motion.div>

          {/* Stack effect: next card slightly visible behind */}
          {nextCard && (
            <div
                className="absolute inset-0 flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2"
                style={{ zIndex: 0 }}
              >
              <div
                className="w-[94%] max-w-[420px] h-full max-h-[calc(100%-0.75rem)] min-h-[200px] rounded-2xl border border-[hsl(var(--border))] overflow-hidden"
                style={{
                  ...cardSkinStyles,
                  background: "hsl(var(--card-bg))",
                  transform: "scale(0.94) translateY(8px)",
                  boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                }}
              >
                <div className="absolute inset-0 flex flex-col items-center justify-center p-4 sm:p-5 text-center">
                  <p className={`${nextSizeClass} text-[hsl(var(--muted-foreground))]/50 line-clamp-3`}>{nextCard.front}</p>
                </div>
              </div>
            </div>
          )}

          {/* Outer: DRAG ONLY — x translation, no rotate/rotateY. Prevents transform conflict on mobile Safari. */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center px-3 py-1.5 sm:px-4 sm:py-2 cursor-grab active:cursor-grabbing z-10"
            style={{ x, touchAction: "pan-y" as const }}
            drag="x"
            dragConstraints={{ left: -500, right: 500 }}
            dragElastic={0.8}
            dragDirectionLock
            onDragStart={handleDragStart}
            onDragEnd={handleDragEnd}
          >
            {/* Middle: tilt feedback (rotate) + perspective for 3D flip. No drag/translate here. */}
            <motion.div
              className="relative w-[94%] max-w-[420px] h-full min-h-[280px] max-h-[calc(100%-0.75rem)]"
              style={{ rotate, WebkitPerspective: "1000px", perspective: "1000px" }}
            >
              {/* Inner: 3D FLIP ONLY — rotateY, no drag. Strict separation for mobile Safari. */}
              <motion.div
                role="button"
                tabIndex={0}
                onClick={() => handleTapFlip()}
                onKeyDown={(e) => e.key === "Enter" && handleTapFlip()}
                className="touch-none rounded-2xl border border-[hsl(var(--border))] relative w-full h-full cursor-pointer"
                style={{
                  ...cardSkinStyles,
                  background: "hsl(var(--card-bg))",
                  WebkitTransformStyle: "preserve-3d",
                  transformStyle: "preserve-3d",
                }}
                animate={{ rotateY: cardFlipped ? 180 : 0 }}
                transition={{ type: "tween", duration: 0.5, ease: "easeInOut" }}
              >
                {/* 3. FRONT FACE — absolute positioning + backface-hidden for iOS Safari 3D fix */}
                <div
                  className="absolute top-0 left-0 w-full h-full rounded-2xl"
                  style={{
                    background: "hsl(var(--card-bg))",
                    WebkitBackfaceVisibility: "hidden",
                    backfaceVisibility: "hidden",
                    pointerEvents: cardFlipped ? "none" : "auto",
                    visibility: cardFlipped ? "hidden" : "visible",
                  }}
                >
                  {/* Inner wrapper for layout and overflow */}
                  <div className="w-full h-full flex flex-col items-center justify-start p-4 sm:p-5 text-center rounded-2xl overflow-auto">
                    {/* Header: hint + icons — stopPropagation on click; touch targets ≥44px */}
                    <div
                      className="flex-shrink-0 w-full flex items-center justify-between gap-2 py-2 min-h-[48px] z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          setIsHintExpanded((v) => !v);
                        }}
                        className={`flex-1 min-w-0 flex items-center min-h-[44px] text-sm py-2 px-3 rounded-full bg-[hsl(var(--bg3))]/50 active:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] transition-all duration-300 ease-in-out overflow-hidden text-left touch-manipulation ${
                          isHintExpanded ? "pr-4 items-start" : "max-w-[140px]"
                        }`}
                        aria-label={t("hint")}
                      >
                        <span className="shrink-0">💡</span>
                        {isHintExpanded ? (
                          <span className="text-xs ml-1.5 border-l border-[hsl(var(--border))] pl-2 whitespace-normal break-words">
                            {hint}
                          </span>
                        ) : (
                          <span className="ml-1.5">{t("hint")}</span>
                        )}
                      </button>
                      <div className="ml-auto flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            if ((card.front || "").trim())
                              void playAudio({
                                card: playbackCard,
                                side: "front",
                                deckLang: deck.lang,
                                volume: voiceVolume ?? 1,
                                onGenerating: () => setLoadingAudioFor("front"),
                                onEnd: () => setLoadingAudioFor(null),
                                onError: () => setLoadingAudioFor(null),
                                onAudioReady: ({ url, locale }) => rememberAudioVariant("front", url, locale),
                                onNotAllowed: () => {
                                  setLoadingAudioFor(null);
                                  toast(t("flashcard.audioRequiresInteraction"));
                                },
                              });
                          }}
                          disabled={loadingAudioFor === "front"}
                          className="relative z-50 flex items-center justify-center w-11 h-11 rounded-full bg-[hsl(var(--bg3))]/60 text-[hsl(var(--foreground))] active:bg-[hsl(var(--bg3))] transition-colors disabled:opacity-70 touch-manipulation"
                          aria-label={t("flashcard.listen")}
                        >
                          {loadingAudioFor === "front" ? (
                            <Loader2 size={20} strokeWidth={2} className="animate-spin" />
                          ) : (
                            <Megaphone size={20} strokeWidth={2} />
                          )}
                        </button>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={toggleFavorite}
                            className={`flex items-center justify-center w-11 h-11 rounded-full transition-colors touch-manipulation ${
                              isFavorited ? "text-amber-400 fill-amber-400" : "text-[hsl(var(--muted-foreground))] active:text-amber-400"
                            }`}
                            aria-label={isFavorited ? t("remove_favorite") : t("add_favorite")}
                          >
                            <Star size={20} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </div>
                    {card.imageUrl && (
                      <div className="w-full flex-shrink-0 rounded-xl overflow-hidden mt-1 mb-3 flex items-center justify-center">
                        <img src={card.imageUrl} alt="" className="w-full h-auto max-h-[40vh] object-contain" />
                      </div>
                    )}
                    <div className={`flex-1 flex items-center justify-center min-h-0 pb-4 ${frontSizeClass}`} style={{ color: "hsl(var(--foreground))" }}>
                      {card.front}
                    </div>
                    <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5 flex-shrink-0 pointer-events-none">{t("click_to_flip")}</p>
                  </div>
                </div>

                {/* 4. BACK FACE: rotateY(180deg) so content faces forward; explicit bg + z-index + pointer-events for iOS Safari */}
                <div
                  className={`absolute top-0 left-0 w-full h-full rounded-2xl overflow-hidden ${cardFlipped ? "z-[20]" : ""}`}
                  style={{
                    background: "hsl(var(--card-bg))",
                    WebkitBackfaceVisibility: "hidden",
                    backfaceVisibility: "hidden",
                    transform: "rotateY(180deg) translateZ(1px)",
                    WebkitTransform: "rotateY(180deg) translateZ(1px)",
                    pointerEvents: cardFlipped ? "auto" : "none",
                    visibility: cardFlipped ? "visible" : "hidden",
                  }}
                >
                  <div className="w-full h-full flex flex-col items-center justify-start p-4 sm:p-5 text-center overflow-hidden rounded-2xl pointer-events-auto">
                    {/* Header: hint + explain + icons — touch targets ≥44px */}
                    <div
                      className="flex-shrink-0 w-full flex items-center justify-between gap-2 py-2 min-h-[48px] z-10"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            setIsHintExpanded((v) => !v);
                          }}
                          className={`flex-1 min-w-0 flex items-center min-h-[44px] text-sm py-2 px-3 rounded-full bg-[hsl(var(--bg3))]/50 active:bg-[hsl(var(--bg3))] text-[hsl(var(--foreground))] transition-all duration-300 ease-in-out overflow-hidden text-left touch-manipulation ${
                            isHintExpanded ? "pr-4 items-start" : "max-w-[140px]"
                          }`}
                          aria-label={t("hint")}
                        >
                          <span className="shrink-0">💡</span>
                          {isHintExpanded ? (
                            <span className="text-xs ml-1.5 border-l border-[hsl(var(--border))] pl-2 whitespace-normal break-words">
                              {hint}
                            </span>
                          ) : (
                            <span className="ml-1.5">{t("hint")}</span>
                          )}
                        </button>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            onShowExplain();
                          }}
                          className="flex items-center justify-center gap-1.5 min-h-[44px] py-2 px-3 rounded-full bg-purple-500/20 text-purple-700 dark:text-purple-300 active:bg-purple-500/30 transition-colors text-sm shrink-0 touch-manipulation"
                          aria-label={t("explain")}
                        >
                          <Sparkles size={16} strokeWidth={2} />
                          {t("explain")}
                        </button>
                      </div>
                      <div className="ml-auto flex items-center gap-1 sm:gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            e.preventDefault();
                            if ((card.back || "").trim())
                              void playAudio({
                                card: playbackCard,
                                side: "back",
                                deckLang: deck.lang,
                                volume: voiceVolume ?? 1,
                                onGenerating: () => setLoadingAudioFor("back"),
                                onEnd: () => setLoadingAudioFor(null),
                                onError: () => setLoadingAudioFor(null),
                                onAudioReady: ({ url, locale }) => rememberAudioVariant("back", url, locale),
                                onNotAllowed: () => {
                                  setLoadingAudioFor(null);
                                  toast(t("flashcard.audioRequiresInteraction"));
                                },
                              });
                          }}
                          disabled={loadingAudioFor === "back"}
                          className="relative z-50 flex items-center justify-center w-11 h-11 rounded-full bg-[hsl(var(--bg3))]/60 text-[hsl(var(--foreground))] active:bg-[hsl(var(--bg3))] transition-colors disabled:opacity-70 touch-manipulation"
                          aria-label={t("flashcard.listen")}
                        >
                          {loadingAudioFor === "back" ? (
                            <Loader2 size={20} strokeWidth={2} className="animate-spin" />
                          ) : (
                            <Megaphone size={20} strokeWidth={2} />
                          )}
                        </button>
                        {!readOnly && (
                          <button
                            type="button"
                            onClick={toggleFavorite}
                            className={`flex items-center justify-center w-11 h-11 rounded-full transition-colors touch-manipulation ${
                              isFavorited ? "text-amber-400 fill-amber-400" : "text-[hsl(var(--muted-foreground))] active:text-amber-400"
                            }`}
                            aria-label={isFavorited ? t("remove_favorite") : t("add_favorite")}
                          >
                            <Star size={20} strokeWidth={1.5} />
                          </button>
                        )}
                      </div>
                    </div>
                    {card.imageUrl && (
                      <div className="w-full flex-shrink-0 rounded-xl overflow-hidden mt-1 mb-3 flex items-center justify-center">
                        <img src={card.imageUrl} alt="" className="w-full h-auto max-h-[40vh] object-contain" />
                      </div>
                    )}
                    <div
                      className={`flex-1 min-h-0 w-full overflow-y-auto break-words whitespace-pre-wrap flex flex-col items-center justify-center px-2 ${frontSizeClass}`}
                      style={{ color: "hsl(var(--foreground))" }}
                    >
                      {card.back}
                      {(card.definition || card.example) && (
                        <div className="mt-3 w-full text-left space-y-1.5 text-sm text-[hsl(var(--muted-foreground))]">
                          {card.definition && (
                            <div>
                              <span className="font-medium text-[hsl(var(--foreground))]/80">{t("deckModal.definitionLabel")}:</span> {card.definition}
                            </div>
                          )}
                          {card.example && (
                            <div>
                              <span className="font-medium text-[hsl(var(--foreground))]/80">{t("deckModal.exampleLabel")}:</span> {card.example}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    <p className="flex-shrink-0 text-xs text-[hsl(var(--muted-foreground))] mt-1.5 pointer-events-none">{t("studyMode.swipeOrTap")}</p>
                  </div>
                </div>
                {/* Dynamic background tint overlay — green (swipe right) / orange (swipe left) */}
                <div className="absolute inset-0 rounded-2xl pointer-events-none overflow-hidden z-[1]">
                  <motion.div
                    className="absolute inset-0 rounded-2xl"
                    style={{ backgroundColor: "rgba(34, 197, 94, 0.25)", opacity: opacityGreen }}
                  />
                  <motion.div
                    className="absolute inset-0 rounded-2xl"
                    style={{ backgroundColor: "rgba(249, 115, 22, 0.25)", opacity: opacityOrange }}
                  />
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </div>

        {/* Bottom control bar: Undo, Auto-play — touch-friendly, safe-area aware */}
        <div className="flex-shrink-0 flex items-center justify-center gap-3 px-4 py-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-background">
          {!readOnly && onUndo && (
            <button
              type="button"
              onClick={onUndo}
              disabled={!canUndo}
              className={`flex items-center justify-center w-12 h-12 rounded-full transition-all touch-manipulation ${
                canUndo
                  ? "text-[hsl(var(--foreground))] active:bg-[hsl(var(--bg3))] active:scale-95"
                  : "text-[hsl(var(--muted-foreground))]/40 cursor-not-allowed"
              }`}
              aria-label={t("studyMode.undo")}
            >
              <RotateCcw size={24} strokeWidth={2} />
            </button>
          )}
          {!readOnly && (
            <button
              type="button"
              onClick={() => setIsAutoPlaying((v) => !v)}
              className={`flex items-center justify-center w-12 h-12 rounded-full transition-all touch-manipulation ${
                isAutoPlaying
                  ? "text-[hsl(var(--primary))] bg-[hsl(var(--primary))]/15"
                  : "text-[hsl(var(--muted-foreground))] active:bg-[hsl(var(--bg3))] active:scale-95"
              }`}
              aria-label={t("studyMode.autoplay")}
            >
              {isAutoPlaying ? <PauseIcon size={24} strokeWidth={2} /> : <PlayIcon size={24} strokeWidth={2} />}
            </button>
          )}
        </div>

        {/* Modals */}
        {isEditingSingleCard && card && onUpdateCard && (
          <EditSingleCardModal
            card={card}
            deckId={deck.id}
            deckTitle={deck.title}
            onSave={(updates) => {
              onUpdateCard(cardIndex, updates);
              setIsEditingSingleCard(false);
            }}
            onClose={() => setIsEditingSingleCard(false)}
          />
        )}
        {pronunciationResult && (
          <PronunciationScoreModal
            said={pronunciationResult.said}
            score={pronunciationResult.score}
            onClose={() => setPronunciationResult(null)}
          />
        )}
      </div>
    );
  }

  return (
    <div className={`relative mx-auto flex w-full min-w-0 flex-col flex-1 min-h-0 ${readOnly ? "max-w-none pb-6 md:pb-2 md:min-h-0" : "max-w-6xl pb-6 md:pb-0"}`}>
      {/* Top Section — compact, clear hierarchy */}
      <div className={`flex-none px-3 pb-0.5 pt-0.5 sm:px-4 sm:pt-1 sm:pb-1 ${readOnly ? "md:pb-0.5" : "md:pt-2 md:pb-1"}`}>
        {onBack && (
          <button
            type="button"
            onClick={onBack}
            className="inline-flex items-center gap-1.5 py-1 bg-transparent border-none text-[hsl(var(--muted-foreground))] text-xs cursor-pointer hover:opacity-80 transition-opacity w-fit sm:text-[13px]"
          >
            <ChevronLeft size={16} strokeWidth={2} />
            {t("dashboard.backToDashboard")}
          </button>
        )}
        <div className={onBack ? "mt-1.5" : "mt-0.5"}>
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <h2 className="min-w-0 flex-1 truncate pr-2 font-[Instrument_Serif] text-[15px] font-semibold text-[hsl(var(--foreground))] sm:text-base md:text-lg md:font-bold md:text-xl">
              {deck.emoji || ""} {deck.title}
            </h2>
            <span className="shrink-0 text-[10px] font-medium tabular-nums text-[hsl(var(--muted-foreground))] sm:text-xs">
              {t("flashcard.cardCounter", { current: cardIndex + 1, total })}
            </span>
          </div>
          <div className="h-1 w-full overflow-hidden rounded-full bg-[hsl(var(--bg3))] sm:h-1.5">
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{
                width: `${pct}%`,
                background: "linear-gradient(90deg, hsl(var(--primary)), hsl(var(--accent2)))",
              }}
            />
          </div>
          {!readOnly && (
            <div className="mt-1 flex justify-center text-[11px] text-[hsl(var(--muted-foreground))]/90 font-medium tracking-wide">
              {t("flashcard.progress", { pct })} &bull;{" "}
              {(() => {
                const rep = card.repetition_count ?? 0;
                const interval = card.interval_days ?? 0;
                return rep === 0 ? t("statistics.status.new") : interval >= 21 ? t("statistics.status.mastered") : t("statistics.status.learning");
              })()}
            </div>
          )}
        </div>
      </div>

      <div className={`flex min-h-0 flex-1 flex-col overflow-hidden px-3 pt-1 sm:px-4 sm:pt-1.5 md:overflow-visible ${readOnly ? "md:gap-2 md:items-stretch md:pt-1.5" : "md:gap-4 md:items-center md:pt-2.5"}`}>
        <div className="flex min-h-0 w-full flex-1 items-center justify-center self-stretch">
          <div className={`relative flex w-full min-w-0 flex-1 min-h-0 self-stretch items-center justify-center ${isReadOnlyDesktop ? "md:min-h-[28rem]" : "min-h-[18.5rem] md:min-h-[max(500px,60vh)]"}`}>
        <motion.div
          className={`relative flex w-full min-w-0 items-center justify-center self-stretch ${isReadOnlyDesktop ? `md:mx-auto ${readOnlyStageWidthClass}` : isReadOnlyMobile ? `h-full md:w-full ${stageWidthClass}` : `h-full md:w-[85vw]`}`}
          style={{ WebkitPerspective: "1000px", perspective: "1000px" }}
          whileTap={isMobile && cardFlipped ? { scale: 0.98 } : undefined}
          transition={{ type: "spring", stiffness: 400, damping: 30 }}
        >
          {/* 2. Inner flipper: rotates strictly on Y axis (inline transform only) */}
          <div
            onClick={handleFlip}
            className={`relative h-full w-full cursor-pointer rounded-2xl transition-transform duration-500 ${cardWidthClass} ${cardShellHeightClass}`}
            style={{
              ...cardSkinStyles,
              background: "hsl(var(--card-bg))",
              WebkitTransformStyle: "preserve-3d",
              transformStyle: "preserve-3d",
              transform: cardFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
            }}
          >
            {/* 3. FRONT FACE — absolute + backface-hidden for iOS Safari 3D fix */}
            <div
              className="absolute left-0 top-0 flex h-full w-full flex-col items-center justify-start overflow-hidden rounded-2xl border border-[hsl(var(--border))] p-3 pb-10 text-center sm:p-4 sm:pb-10 md:px-3.5 md:pt-4 md:pb-0"
              style={{
                background: "hsl(var(--card-bg))",
                WebkitBackfaceVisibility: "hidden",
                backfaceVisibility: "hidden",
                pointerEvents: cardFlipped ? "none" : "auto",
                visibility: cardFlipped ? "hidden" : "visible",
              }}
            >
              {/* Header: hint + quick actions */}
              <div className="z-10 mb-0.5 grid w-full flex-shrink-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-1.5 sm:mb-0.5 sm:gap-2">
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    setIsHintExpanded((v) => !v);
                  }}
                  className={`flex min-w-0 flex-1 items-center overflow-hidden rounded-full bg-[hsl(var(--bg3))]/50 text-left text-[hsl(var(--foreground))] transition-all duration-300 ease-in-out hover:bg-[hsl(var(--bg3))] ${
                    isHintExpanded ? "items-start px-3 py-2 pr-4" : "max-w-[120px] px-3 py-1.5 text-xs sm:max-w-[140px] sm:text-sm"
                  }`}
                  aria-label={t("hint")}
                >
                  <span className="shrink-0">💡</span>
                  {isHintExpanded ? (
                    <span className="text-xs ml-1.5 border-l border-[hsl(var(--border))] pl-2 whitespace-normal break-words">
                      {hint}
                    </span>
                  ) : (
                    <span className="ml-1.5 truncate">{t("hint")}</span>
                  )}
                </button>
                {/* Top-right quick actions — never shrink */}
                <div className="z-10 ml-auto flex flex-shrink-0 items-center justify-self-end gap-1 sm:gap-2">
                {onUpdateCard && !readOnly && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      setIsEditingSingleCard(true);
                    }}
                    className={quickActionBtnClass}
                    title={t("edit_card")}
                    aria-label={t("edit_card")}
                  >
                    <Pencil size={18} strokeWidth={1.5} />
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    if ((card.front || "").trim())
                      void playAudio({
                        card: playbackCard,
                        side: "front",
                        deckLang: deck.lang,
                        volume: voiceVolume ?? 1,
                        onGenerating: () => setLoadingAudioFor("front"),
                        onEnd: () => setLoadingAudioFor(null),
                        onError: () => setLoadingAudioFor(null),
                        onAudioReady: ({ url, locale }) => rememberAudioVariant("front", url, locale),
                        onNotAllowed: () => {
                          setLoadingAudioFor(null);
                          toast(t("flashcard.audioRequiresInteraction"));
                        },
                      });
                  }}
                  disabled={loadingAudioFor === "front"}
                  className={ttsBtnClass}
                  title={t("flashcard.listen")}
                  aria-label={t("flashcard.listen")}
                >
                  {loadingAudioFor === "front" ? (
                    <Loader2 size={18} strokeWidth={1.5} className="animate-spin" />
                  ) : (
                    <Megaphone size={18} strokeWidth={1.5} />
                  )}
                </button>
                {!readOnly && (
                  <button
                    type="button"
                    onClick={toggleFavorite}
                    className={quickActionBtnClass}
                    title={isFavorited ? t("remove_favorite") : t("add_favorite")}
                    aria-label={isFavorited ? t("remove_favorite") : t("add_favorite")}
                  >
                    <Star
                      size={18}
                      strokeWidth={1.5}
                      className={isFavorited ? "fill-amber-400 text-amber-400" : ""}
                    />
                  </button>
                )}
                </div>
              </div>
              {card.imageUrl && (
                <div className={`mx-auto mt-0.5 flex w-full flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[hsl(var(--bg3))]/15 px-1 md:mt-1 md:bg-[hsl(var(--bg3))]/20 ${mediaHeightClass}`}>
                  <img
                    src={card.imageUrl}
                    alt=""
                    className={`h-full w-full object-contain rounded-lg ${mediaHeightClass}`}
                  />
                </div>
              )}
              <div
                className={`flex min-h-[4rem] w-full flex-1 items-center justify-center overflow-y-auto break-words whitespace-pre-wrap px-3 pb-1.5 text-center sm:px-4 sm:pb-2 mt-1 sm:mt-1.5 md:mt-3 md:min-h-[4.5rem] md:px-3.5 md:pb-5 md:mb-2.5 ${frontSizeClass}`}
                style={{ color: "hsl(var(--foreground))", fontWeight: 400, ...frontTextStyle }}
              >
                {card.front}
              </div>
              <div className="mt-auto w-full flex-shrink-0 rounded-b-2xl bg-indigo-900/70 py-1.5 text-center text-[10px] font-medium text-white/95 sm:py-2 sm:text-xs md:-mx-4 md:w-[calc(100%+2rem)] md:bg-indigo-900/80 md:py-4">
                {t("click_to_flip")}
              </div>
            </div>

            {/* 4. BACK FACE: rotateY(180deg) + explicit bg + z-index + pointer-events for iOS Safari */}
            <div
              className={`absolute left-0 top-0 h-full w-full overflow-hidden rounded-2xl border border-[hsl(var(--border))] ${cardFlipped ? "z-[20]" : ""}`}
              style={{
                background: "hsl(var(--card-bg))",
                WebkitBackfaceVisibility: "hidden",
                backfaceVisibility: "hidden",
                transform: "rotateY(180deg) translateZ(1px)",
                WebkitTransform: "rotateY(180deg) translateZ(1px)",
                pointerEvents: cardFlipped ? "auto" : "none",
                visibility: cardFlipped ? "visible" : "hidden",
              }}
            >
              <div className="pointer-events-auto flex h-full w-full flex-col items-center justify-start overflow-hidden p-3 pb-10 sm:p-4 sm:pb-10 md:px-3.5 md:pt-4 md:pb-0">
              <div
                className="z-10 mb-0.5 grid w-full flex-shrink-0 grid-cols-[minmax(0,1fr)_auto] items-start gap-1.5 sm:mb-1 sm:gap-2"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 sm:gap-2">
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsHintExpanded((v) => !v);
                    }}
                    className={`flex min-w-0 flex-1 items-center overflow-hidden rounded-full bg-[hsl(var(--bg3))]/50 text-left text-[hsl(var(--foreground))] transition-all duration-300 ease-in-out hover:bg-[hsl(var(--bg3))] ${
                      isHintExpanded ? "items-start px-3 py-2 pr-4" : "max-w-[120px] px-3 py-1.5 text-xs sm:max-w-[140px] sm:text-sm"
                    }`}
                    aria-label={t("hint")}
                  >
                    <span className="shrink-0">💡</span>
                    {isHintExpanded ? (
                      <span className="text-xs ml-1.5 border-l border-[hsl(var(--border))] pl-2 whitespace-normal break-words">
                        {hint}
                      </span>
                    ) : (
                      <span className="ml-1.5 truncate">{t("hint")}</span>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      onShowExplain();
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    className="inline-flex shrink-0 items-center justify-center gap-1 rounded-full bg-purple-500/20 px-3 py-1.5 text-xs text-purple-700 transition-colors hover:bg-purple-500/30 dark:text-purple-300 sm:text-sm"
                    aria-label={t("explain")}
                  >
                    <Sparkles size={15} strokeWidth={2} />
                    {t("explain")}
                  </button>
                </div>
                <div className="flex flex-shrink-0 items-center gap-1 sm:gap-2">
                  {onUpdateCard && !readOnly && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setIsEditingSingleCard(true);
                      }}
                      className={quickActionBtnClass}
                      title={t("edit_card")}
                      aria-label={t("edit_card")}
                    >
                      <Pencil size={18} strokeWidth={1.5} />
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      e.preventDefault();
                      if ((card.back || "").trim())
                        void playAudio({
                          card: playbackCard,
                          side: "back",
                          deckLang: deck.lang,
                          volume: voiceVolume ?? 1,
                          onGenerating: () => setLoadingAudioFor("back"),
                          onEnd: () => setLoadingAudioFor(null),
                          onError: () => setLoadingAudioFor(null),
                          onAudioReady: ({ url, locale }) => rememberAudioVariant("back", url, locale),
                          onNotAllowed: () => {
                            setLoadingAudioFor(null);
                            toast(t("flashcard.audioRequiresInteraction"));
                          },
                        });
                    }}
                    onPointerDown={(e) => e.stopPropagation()}
                    disabled={loadingAudioFor === "back"}
                    className={ttsBtnClass}
                    title={t("flashcard.listen")}
                    aria-label={t("flashcard.listen")}
                  >
                    {loadingAudioFor === "back" ? (
                      <Loader2 size={18} strokeWidth={1.5} className="animate-spin" />
                    ) : (
                      <Megaphone size={18} strokeWidth={1.5} />
                    )}
                  </button>
                  {!readOnly && (
                    <button
                      type="button"
                      onClick={toggleFavorite}
                      className={quickActionBtnClass}
                      title={isFavorited ? t("remove_favorite") : t("add_favorite")}
                      aria-label={isFavorited ? t("remove_favorite") : t("add_favorite")}
                    >
                      <Star
                        size={18}
                        strokeWidth={1.5}
                        className={isFavorited ? "fill-amber-400 text-amber-400" : ""}
                      />
                    </button>
                  )}
                </div>
              </div>
              {card.imageUrl && (
                <div className={`mx-auto mt-0.5 flex w-full flex-shrink-0 items-center justify-center overflow-hidden rounded-xl bg-[hsl(var(--bg3))]/15 px-1 md:mt-1 md:bg-[hsl(var(--bg3))]/20 ${mediaHeightClass}`}>
                  <img
                    src={card.imageUrl}
                    alt=""
                    className={`h-full w-full object-contain rounded-lg ${mediaHeightClass}`}
                  />
                </div>
              )}
              <div
                className={`mt-1.5 flex min-h-[4rem] w-full flex-1 items-center justify-center overflow-y-auto break-words whitespace-pre-wrap px-3 pb-1.5 pt-1 text-center not-italic font-normal sm:mt-1.5 sm:px-4 sm:pb-2 md:mt-3 md:min-h-[4.5rem] md:px-3.5 md:pb-5 md:mb-2.5 ${frontSizeClass}`}
                style={{ color: "hsl(var(--foreground))", fontWeight: 400, textAlign: "center", ...backTextStyle }}
              >
                {card.back}
                {(card.definition || card.example) && (
                  <div className="mt-3 text-left space-y-1.5 text-sm text-[hsl(var(--muted-foreground))]">
                    {card.definition && (
                      <div>
                        <span className="font-medium text-[hsl(var(--foreground))]/80">{t("deckModal.definitionLabel")}:</span> {card.definition}
                      </div>
                    )}
                    {card.example && (
                      <div>
                        <span className="font-medium text-[hsl(var(--foreground))]/80">{t("deckModal.exampleLabel")}:</span> {card.example}
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="w-full flex-shrink-0 rounded-b-2xl bg-indigo-900/70 py-1.5 text-center text-[10px] font-medium text-white/95 sm:py-2 sm:text-xs md:-mx-4 md:w-[calc(100%+2rem)] md:bg-indigo-900/80 md:py-4">
                {t("click_to_flip")}
              </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Card-area navigation arrows — overlay card, left/right, inward on desktop (closer to content) */}
        {(onPrevCard || onNextCard) && (
          <>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onPrevCard?.(); }}
              disabled={cardIndex === 0 || !onPrevCard}
              className="absolute left-4 sm:left-6 md:left-6 top-1/2 z-10 flex h-9 w-9 md:h-10 md:w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[hsl(var(--border))]/70 bg-[hsl(var(--card-bg))]/95 shadow-md shadow-black/5 backdrop-blur-sm text-[hsl(var(--foreground))] transition-all hover:bg-[hsl(var(--bg3))]/90 hover:border-[hsl(var(--border))] active:scale-95 disabled:pointer-events-none disabled:opacity-30"
              aria-label={t("flashcard.previousCard")}
            >
              <ArrowLeft className="h-4 w-4 sm:h-[18px] sm:w-[18px] md:h-5 md:w-5" strokeWidth={2.5} />
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); onNextCard?.(); }}
              disabled={cardIndex >= total - 1 || !onNextCard}
              className="absolute right-4 sm:right-6 md:right-6 top-1/2 z-10 flex h-9 w-9 md:h-10 md:w-10 -translate-y-1/2 items-center justify-center rounded-full border border-[hsl(var(--border))]/70 bg-[hsl(var(--card-bg))]/95 shadow-md shadow-black/5 backdrop-blur-sm text-[hsl(var(--foreground))] transition-all hover:bg-[hsl(var(--bg3))]/90 hover:border-[hsl(var(--border))] active:scale-95 disabled:pointer-events-none disabled:opacity-30"
              aria-label={t("flashcard.nextCard")}
            >
              <ArrowRight className="h-4 w-4 sm:h-[18px] sm:w-[18px] md:h-5 md:w-5" strokeWidth={2.5} />
            </button>
          </>
        )}
        </div>
        </div>

        {/* Desktop action bar — visible in viewport, compact toolbar */}
        <div className="hidden w-full max-w-6xl flex-col gap-3 md:flex flex-shrink-0 md:pt-1.5 md:pb-1">
        {!readOnly && (
        <div className="flex items-center justify-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={recordPronunciation}
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-[hsl(var(--border))]/80 bg-[hsl(var(--bg3))]/60 text-[hsl(var(--foreground))] text-xs font-medium cursor-pointer transition-all duration-200 hover:bg-[hsl(var(--bg3))] hover:border-[hsl(var(--primary))]/40 hover:text-[hsl(var(--primary))]"
            style={{ fontFamily: "DM Sans, sans-serif" }}
          >
            {isRecording ? "⏺" : "🎤"} {isRecording ? t("flashcard.recording") : t("flashcard.record")}
          </button>
          <button
            onClick={onShuffle}
            className="flex items-center justify-center w-9 h-9 rounded-full border border-[hsl(var(--border))]/80 bg-[hsl(var(--bg3))]/60 hover:bg-[hsl(var(--bg3))] hover:border-[hsl(var(--border))] text-[hsl(var(--foreground))] transition-colors shrink-0"
            aria-label={t("flashcard.shuffle")}
            title={t("flashcard.shuffle")}
          >
            <Shuffle size={16} strokeWidth={2} />
          </button>
          <button
            onClick={() => setIsAutoPlaying((v) => !v)}
            className={`flex items-center justify-center w-9 h-9 rounded-full border transition-colors shrink-0 ${
              isAutoPlaying
                ? "border-[hsl(var(--primary))]/60 bg-[hsl(var(--primary))]/15 text-[hsl(var(--primary))]"
                : "border-[hsl(var(--border))]/80 bg-[hsl(var(--bg3))]/60 hover:bg-[hsl(var(--bg3))] hover:border-[hsl(var(--border))] text-[hsl(var(--foreground))]"
            }`}
            aria-label={t("autoplay")}
            title={t("autoplay")}
          >
            {isAutoPlaying ? (
              <PauseIcon size={16} strokeWidth={2} />
            ) : (
              <PlayIcon size={16} strokeWidth={2} />
            )}
          </button>
        </div>
        )}
        {readOnly && cardFlipped && (
          <div className="flex gap-2 justify-center flex-wrap py-1">
            <button
              type="button"
              onClick={() => handleMark(false)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-rose-400/40 bg-rose-500/10 text-rose-500 hover:bg-rose-500/15 text-xs font-medium cursor-pointer transition-all duration-200"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              ✗ {t("shared.incorrect")}
            </button>
            <button
              type="button"
              onClick={() => handleMark(true)}
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full border border-emerald-400/40 bg-emerald-500/10 text-emerald-600 hover:bg-emerald-500/15 text-xs font-medium cursor-pointer transition-all duration-200"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              ✓ {t("shared.correct")}
            </button>
          </div>
        )}
        </div>
      </div>

      {/* Mobile layout: compact nav + readOnly Correct/Incorrect */}
      <div className="z-10 flex-shrink-0 px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] pt-1 sm:px-4 md:pt-0 md:pb-2">
      <div className="flex flex-col items-center gap-2 md:hidden">
        {readOnly && cardFlipped && (
          <div className="flex w-full max-w-[320px] justify-center gap-3">
            <button
              type="button"
              onClick={() => handleMark(false)}
              className="inline-flex flex-1 items-center justify-center gap-1.5 min-h-[44px] rounded-full border border-rose-400/50 bg-rose-500/10 py-3 text-[13px] font-medium text-rose-500 transition-all duration-200 active:bg-rose-500/20 touch-manipulation"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              ✗ {t("shared.incorrect")}
            </button>
            <button
              type="button"
              onClick={() => handleMark(true)}
              className="inline-flex flex-1 items-center justify-center gap-1.5 min-h-[44px] rounded-full border border-emerald-400/50 bg-emerald-500/10 py-3 text-[13px] font-medium text-emerald-600 transition-all duration-200 active:bg-emerald-500/20 touch-manipulation"
              style={{ fontFamily: "DM Sans, sans-serif" }}
            >
              ✓ {t("shared.correct")}
            </button>
        </div>
        )}
        <p className="text-center text-[10px] text-[hsl(var(--muted-foreground))] sm:text-[11px]">
          {cardFlipped ? t("flashcard.useArrows") : t("flashcard.flipFirst")}
        </p>
      </div>

      </div>

      {/* Single-card edit modal */}
      {isEditingSingleCard && card && onUpdateCard && (
        <EditSingleCardModal
          card={card}
          deckId={deck.id}
          deckTitle={deck.title}
          onSave={(updates) => {
            onUpdateCard(cardIndex, updates);
            setIsEditingSingleCard(false);
          }}
          onClose={() => setIsEditingSingleCard(false)}
        />
      )}

      {/* Pronunciation Score Modal */}
      {pronunciationResult && (
        <PronunciationScoreModal
          said={pronunciationResult.said}
          score={pronunciationResult.score}
          onClose={() => setPronunciationResult(null)}
        />
      )}
    </div>
  );
}
